import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Alert, Box, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  AdresseFacturationCompte, AdresseFacturationSaisie, BdcDossierManuel,
  recupererFacturationDossierManuel, enregistrerAdresseFacturationDossierManuel,
} from '../Services';
import { messageErreurs } from '../services/formulairesServices';

// Adresse de facturation d'un dossier créé manuellement, saisie par l'admin comme le client le fait dans l'appli
// (« Information de facturation ») : mêmes champs, choix parmi les adresses du compte ou nouvelle adresse. Valider crée
// le bon de commande du dossier (0 €, les dossiers manuels n'ont pas de prix) ou met à jour son adresse ; rien d'autre
// (ni état, ni paiement, ni mail). Non modifiable une fois le BDC payé.

interface AdresseFacturationDossierDialogProps {
  open: boolean;
  demandeId: number | null;
  onClose: () => void;
  onEnregistre: () => void | Promise<void>; // après enregistrement (rechargement de la liste : icône « Facturer »)
}

const VIDE: AdresseFacturationSaisie = {
  nom: '', adresse_ligne1: '', adresse_ligne2: '', ville: '', code_postal: '', pays: '', telephone: '',
};

const versSaisie = (a: Partial<AdresseFacturationSaisie>): AdresseFacturationSaisie => ({
  nom: a.nom || '',
  adresse_ligne1: a.adresse_ligne1 || '',
  adresse_ligne2: a.adresse_ligne2 || '',
  ville: a.ville || '',
  code_postal: a.code_postal || '',
  pays: a.pays || '',
  telephone: a.telephone || '',
});

const memeAdresse = (a: AdresseFacturationSaisie, b: AdresseFacturationSaisie) =>
  (Object.keys(VIDE) as (keyof AdresseFacturationSaisie)[]).every((k) => (a[k] || '').trim() === (b[k] || '').trim());

const AdresseFacturationDossierDialog: React.FC<AdresseFacturationDossierDialogProps> = ({ open, demandeId, onClose, onEnregistre }) => {
  const [chargement, setChargement] = useState(false);
  const [adresses, setAdresses] = useState<AdresseFacturationCompte[]>([]);
  const [bdc, setBdc] = useState<BdcDossierManuel | null>(null);
  const [idAdresse, setIdAdresse] = useState(0); // 0 = nouvelle adresse
  const [adresse, setAdresse] = useState<AdresseFacturationSaisie>(VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);

  // À l'ouverture : adresses du compte + BDC éventuel. Pré-remplissage : l'adresse du BDC s'il y en a une (rattachée à
  // l'adresse du compte identique), sinon la première adresse du compte, comme dans l'appli
  useEffect(() => {
    if (!open || !demandeId) return undefined;
    let actif = true;
    setChargement(true);
    setErreur(null);
    setAdresses([]);
    setBdc(null);
    setIdAdresse(0);
    setAdresse(VIDE);
    recupererFacturationDossierManuel(demandeId)
      .then((r) => {
        if (!actif) return;
        setAdresses(r.adresses);
        setBdc(r.bdc);
        if (r.bdc && r.bdc.nom) {
          const saisie = versSaisie(r.bdc);
          const identique = r.adresses.find((a) => memeAdresse(versSaisie(a), saisie));
          setAdresse(saisie);
          setIdAdresse(identique ? identique.id : 0);
        } else if (r.adresses.length > 0) {
          setAdresse(versSaisie(r.adresses[0]));
          setIdAdresse(r.adresses[0].id);
        }
      })
      .catch((e) => { if (actif) setErreur(messageErreurs(e, 'Chargement impossible')); })
      .finally(() => { if (actif) setChargement(false); });
    return () => { actif = false; };
  }, [open, demandeId]);

  const paye = !!bdc && bdc.etat === 'payé';
  const verrouille = chargement || enregistrement || paye;
  const complet = !!(adresse.nom.trim() && adresse.adresse_ligne1.trim() && adresse.ville.trim()
    && adresse.code_postal.trim() && adresse.pays.trim() && adresse.telephone.trim());

  const choisirAdresse = (id: number) => {
    const a = adresses.find((x) => x.id === id);
    if (!a) return;
    setIdAdresse(a.id);
    setAdresse(versSaisie(a));
  };

  const nouvelleAdresse = () => {
    setIdAdresse(0);
    setAdresse(VIDE);
  };

  const champ = (cle: keyof AdresseFacturationSaisie) => ({
    value: adresse[cle],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setAdresse({ ...adresse, [cle]: e.target.value }),
    disabled: verrouille,
    fullWidth: true,
    size: 'small' as const,
  });

  const valider = async () => {
    if (!demandeId || !complet || verrouille) return;
    setEnregistrement(true);
    setErreur(null);
    try {
      await enregistrerAdresseFacturationDossierManuel(demandeId, adresse, idAdresse);
      onClose();
      await onEnregistre();
    } catch (e) {
      console.error("Erreur enregistrement de l'adresse de facturation", e);
      setErreur(messageErreurs(e, "Enregistrement de l'adresse impossible"));
    } finally {
      setEnregistrement(false);
    }
  };

  const fermer = () => { if (!enregistrement) onClose(); };

  return (
    <Dialog open={open} onClose={fermer} fullWidth maxWidth="sm">
      <DialogTitle>Adresse de facturation</DialogTitle>
      <DialogContent>
        {chargement ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {paye ? (
              <Alert severity="warning">Bon de commande n° {bdc?.id} payé : l'adresse n'est plus modifiable.</Alert>
            ) : bdc ? (
              <Alert severity="info">
                Bon de commande n° {bdc.id} ({bdc.etat}) : l'adresse sera mise à jour sur le bon de commande et sur le compte du client.
              </Alert>
            ) : (
              <Alert severity="info">
                Valider crée le bon de commande du dossier à 0 € (les dossiers créés manuellement n'ont pas de prix).
                Facturez-le ensuite avec l'icône « Facturer ».
              </Alert>
            )}
            {erreur && <Alert severity="error" onClose={() => setErreur(null)}>{erreur}</Alert>}

            {adresses.length > 0 && (
              <TextField
                select
                label="Choisir une adresse"
                value={idAdresse ? String(idAdresse) : ''}
                onChange={(e) => choisirAdresse(Number(e.target.value))}
                disabled={verrouille}
                fullWidth
                size="small"
                helperText={idAdresse ? 'Adresse du compte du client (mise à jour si vous la modifiez)' : 'Nouvelle adresse, ajoutée au compte du client'}
              >
                {adresses.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>{a.nom} — {a.adresse_ligne1}, {a.code_postal} {a.ville}</MenuItem>
                ))}
              </TextField>
            )}

            <TextField label="Nom ou raison sociale" required {...champ('nom')} />
            <TextField label="Adresse Ligne 1" required {...champ('adresse_ligne1')} />
            <TextField label="Adresse Ligne 2 (optionnel)" {...champ('adresse_ligne2')} />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField label="Ville" required {...champ('ville')} />
              <TextField label="Code Postal" required {...champ('code_postal')} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField label="Pays" required {...champ('pays')} />
              <TextField label="Téléphone" required {...champ('telephone')} />
            </Box>

            {adresses.length > 0 && !paye && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={nouvelleAdresse} disabled={verrouille} sx={{ textTransform: 'none' }}>
                Créer une nouvelle adresse
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fermer} disabled={enregistrement}>{paye ? 'Fermer' : 'Annuler'}</Button>
        {!paye && (
          <Button
            variant="contained"
            onClick={valider}
            disabled={!complet || verrouille}
            startIcon={enregistrement ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Valider
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdresseFacturationDossierDialog;
